import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import Logo from '../../components/Logo/Logo';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Privacy Policy - PharmaChargeback"
        description="Learn about how PharmaChargeback protects your data and privacy. Our comprehensive privacy policy outlines our data collection, usage, and protection practices."
        keywords="privacy policy, data protection, HIPAA compliance, pharmaceutical data security"
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/">
                <Logo variant="color" size="lg" />
              </Link>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-500 hover:text-gray-900">Home</Link>
              <Link to="/about" className="text-gray-500 hover:text-gray-900">About</Link>
              <Link to="/contact" className="text-gray-500 hover:text-gray-900">Contact</Link>
              <Link to="/login" className="text-gray-500 hover:text-gray-900">Sign In</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Last updated: January 15, 2024</p>
        
        <div className="prose prose-lg max-w-none">
          <h2>1. Introduction</h2>
          <p>
            PharmaChargeback ("we," "our," or "us") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your 
            information when you use our pharmaceutical chargeback management platform.
          </p>

          <h2>2. Information We Collect</h2>
          <h3>2.1 Personal Information</h3>
          <p>We may collect the following types of personal information:</p>
          <ul>
            <li>Name and contact information (email, phone, address)</li>
            <li>Company information and job title</li>
            <li>Account credentials and authentication data</li>
            <li>Payment and billing information</li>
          </ul>

          <h3>2.2 Pharmaceutical Data</h3>
          <p>We process pharmaceutical industry data including:</p>
          <ul>
            <li>Chargeback and rebate information</li>
            <li>Contract and pricing data</li>
            <li>Sales and transaction records</li>
            <li>Product and customer information</li>
            <li>EDI transaction data</li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain our services</li>
            <li>Process chargebacks and manage contracts</li>
            <li>Generate reports and analytics</li>
            <li>Ensure compliance with regulations</li>
            <li>Improve our platform and services</li>
            <li>Communicate with you about our services</li>
          </ul>

          <h2>4. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your data:
          </p>
          <ul>
            <li>End-to-end encryption for data transmission and storage</li>
            <li>HIPAA-compliant infrastructure and processes</li>
            <li>SOC 2 Type II certified security controls</li>
            <li>Regular security audits and assessments</li>
            <li>Access controls and authentication protocols</li>
          </ul>

          <h2>5. Data Sharing</h2>
          <p>We do not sell your personal information. We may share information with:</p>
          <ul>
            <li>Service providers who assist in our operations</li>
            <li>Regulatory authorities when required by law</li>
            <li>Business partners with your explicit consent</li>
            <li>Legal authorities in response to valid legal requests</li>
          </ul>

          <h2>6. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Correct inaccurate data</li>
            <li>Request data deletion</li>
            <li>Object to data processing</li>
            <li>Data portability</li>
            <li>Withdraw consent</li>
          </ul>

          <h2>7. Compliance</h2>
          <p>
            We comply with applicable data protection laws including:
          </p>
          <ul>
            <li>Health Insurance Portability and Accountability Act (HIPAA)</li>
            <li>General Data Protection Regulation (GDPR)</li>
            <li>California Consumer Privacy Act (CCPA)</li>
            <li>Other applicable federal and state regulations</li>
          </ul>

          <h2>8. Data Retention</h2>
          <p>
            We retain your data only as long as necessary for the purposes outlined 
            in this policy or as required by law. Pharmaceutical data may be retained 
            longer to comply with regulatory requirements.
          </p>

          <h2>9. International Transfers</h2>
          <p>
            If you are located outside the United States, please note that your 
            information may be transferred to and processed in the United States. 
            We ensure appropriate safeguards are in place for such transfers.
          </p>

          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you 
            of any changes by posting the new Privacy Policy on this page and updating 
            the "Last updated" date.
          </p>

          <h2>11. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <ul>
            <li>Email: privacy@pharmachargeback.com</li>
            <li>Phone: +1 (555) 123-4567</li>
            <li>Address: 123 Healthcare Plaza, Suite 500, Boston, MA 02108</li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <Logo variant="color" size="lg" className="mb-4" />
              <p className="text-gray-400 mb-4">
                Streamlining pharmaceutical chargeback management for the modern era.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/features" className="text-gray-400 hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-white">Pricing</Link></li>
                <li><Link to="/demo" className="text-gray-400 hover:text-white">Demo</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white">About Us</Link></li>
                <li><Link to="/careers" className="text-gray-400 hover:text-white">Careers</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/contact" className="text-gray-400 hover:text-white">Contact Us</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 PharmaChargeback. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
